import { Component, Input, inject } from "@angular/core";
import { MatDialogRef } from "@angular/material/dialog";

import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { ActionButton } from "@bitwarden/web-vault/app/components/buttons/gloss-button/actionButton";
import { ManualInstitution } from "@bitwarden/web-vault/app/components/manually-add-institution/manual-institution";
import { InstitutionStoreModel } from "@bitwarden/web-vault/app/models/store/institution.store.model";
import { Country, GlossInputOptions } from "@bitwarden/web-vault/app/models/types/general-types";
import { getLocationCurrencyFromCountryCode } from "@bitwarden/web-vault/app/models/types/location-currency.types";
import { InstitutionView } from "@bitwarden/web-vault/app/models/view/institution/institution.view";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { defaultInputOptions } from "@bitwarden/web-vault/app/gloss/manage/manage-accounts/accounts-add-edit/component.options";
import { getDefaultAccount } from "@bitwarden/web-vault/app/shared/utils/helper.institution/default-institution-account";
import { DataPluginStoreService } from "@bitwarden/web-vault/app/services/store/data-plugin/data-plugin.store.service";

@Component({
  selector: "app-manually-add-institution",
  templateUrl: "./manually-add-institution.component.html",
})
export class ManuallyAddInstitutionComponent {
  @Input() selectedCountry!: Country;

  // Injected services
  private readonly userStore = inject(UserStoreService);
  private readonly i18nService = inject(I18nService);
  private readonly dialogRef = inject(MatDialogRef<ManuallyAddInstitutionComponent>);
  private readonly manualInstitution = inject(ManualInstitution);
  private readonly dataPluginStore = inject(DataPluginStoreService);

  // Local state
  private country?: Country;
  private institutionName = "";

  // UI options
  readonly inputOptions: GlossInputOptions = {
    ...defaultInputOptions,
    isRequired: true,
    placeholder: "Type name...",
    label: this.i18nService.t("institutionName"),
    inputBlurred: this.onInstitutionName.bind(this),
  };

  readonly saveButtonOptions = new ActionButton({
    text: this.i18nService.t("saveAndContinue"),
    isEnabled: true,
    onClick: this.saveAndContinue.bind(this),
  });

  // Handlers
  async setCountry(country: Country) {
    this.country = country;
    await this.dataPluginStore.theInstitutionList.fetch({ countryCode: country.code });
  }

  onInstitutionName(instoName: string): void {
    this.institutionName = instoName;
  }

  async saveAndContinue(): Promise<void> {
    // account-add-edit.component.ts's addInstitution() resolves the "Other" dropdown sentinel to
    // a real country before this component ever sees selectedCountry, so this dialog only needs
    // to fall through to whichever country it was actually handed - its own picker's active
    // selection if the user touched it, otherwise the preselected value. That resolution itself
    // can come back undefined (a countryViews() lookup miss), and this.selectedCountry can be
    // null (initializeCountry() sets it that way when theCountryViews() hasn't loaded or doesn't
    // contain the user's location), so neither can be dereferenced unguarded (#4468).
    const cc =
      this.country?.code ??
      this.selectedCountry?.code ??
      this.userStore.preferences.preferenceView()?.userLocation ??
      "AU";

    const instoStoreModel: InstitutionStoreModel = {
      name: this.institutionName,
      fk: null,
      fskId: null,
      vid: "",
      dc: "",
      dm: "",
      id: crypto.randomUUID(),
      v: 4,
      sq_lg_url: "",
      fl_lg_url: "",
      swt: {
        brc: "",
        bac: "",
        cc,
        lc: "",
      },
      bic: {
        bc: "",
        cc,
        lc: "",
      },
      avaAcc: [],
      org: "manual",
      lnkT: null,
    };

    const institutionView = new InstitutionView(instoStoreModel);
    institutionView.availableAccounts[0] = getDefaultAccount(
      institutionView.id,
      getLocationCurrencyFromCountryCode(cc),
    );

    if (this.manualInstitution.isValid(institutionView)) {
      const storedInsto = await this.userStore.institutions.save(institutionView);
      this.dialogRef.close(storedInsto);
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
